import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Shield,
  Lock,
  AlertTriangle,
  CheckCircle,
  Activity,
  Fingerprint,
  Users,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  getPlatformSecurity,
  type PlatformSecurity,
} from '../../services/platformAnalyticsService';

const fmtTime = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  tone?: 'default' | 'warn' | 'good';
}> = ({ label, value, sub, icon, tone = 'default' }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            tone === 'warn'
              ? 'text-amber-500'
              : tone === 'good'
                ? 'text-green-600 dark:text-green-400'
                : 'text-muted-foreground'
          }
        >
          {icon}
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

// Colour an audit action by what it implies.
const actionTone = (action: string): string => {
  const a = action.toLowerCase();
  if (a.includes('fail') || a.includes('lock')) return 'bg-red-100 dark:bg-red-500/15 text-red-700';
  if (a.includes('mfa') || a.includes('2fa')) return 'bg-blue-100 dark:bg-blue-500/15 text-blue-700';
  if (a.includes('password')) return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700';
  return 'bg-zinc-100 text-zinc-700';
};

export const SecurityPage: React.FC = () => {
  const [data, setData] = useState<PlatformSecurity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    getPlatformSecurity()
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) =>
        setError(e?.response?.data?.message || 'Could not load security data.'),
      )
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const t = data?.totals;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold">Security</h1>
            <p className="text-sm text-muted-foreground">
              Live authentication & account security across all tenants.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Metric
          label="Two-factor adoption"
          value={`${t?.mfaAdoptionPct ?? 0}%`}
          sub={`${t?.mfaEnabled ?? 0} of ${t?.activeUsers ?? 0} active users`}
          icon={<Fingerprint className="h-4 w-4" />}
          tone={(t?.mfaAdoptionPct ?? 0) >= 60 ? 'good' : 'warn'}
        />
        <Metric
          label="Locked accounts"
          value={t?.lockedAccounts ?? 0}
          sub="currently locked by failed logins"
          icon={<Lock className="h-4 w-4" />}
          tone={(t?.lockedAccounts ?? 0) > 0 ? 'warn' : 'good'}
        />
        <Metric
          label="Accounts accumulating failures"
          value={t?.usersWithFailedAttempts ?? 0}
          sub={`${t?.totalFailedAttempts ?? 0} failed attempts on record`}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone={(t?.usersWithFailedAttempts ?? 0) > 0 ? 'warn' : 'good'}
        />
        <Metric
          label="Active sessions"
          value={t?.activeSessions ?? 0}
          sub="unexpired, active sessions"
          icon={<Activity className="h-4 w-4" />}
        />
        <Metric
          label="Sign-ins (24h)"
          value={t?.logins24h ?? 0}
          sub="users who logged in today"
          icon={<Users className="h-4 w-4" />}
        />
        <Metric
          label="Active users"
          value={t?.activeUsers ?? 0}
          sub="across all tenants"
          icon={<CheckCircle className="h-4 w-4" />}
        />
      </div>

      {/* Locked accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4" /> Locked accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.lockedAccounts.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No accounts are currently locked. 🎉
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">User</th>
                    <th className="py-2 pr-4 font-medium">Tenant</th>
                    <th className="py-2 pr-4 font-medium">Failed attempts</th>
                    <th className="py-2 pr-4 font-medium">Locked until</th>
                    <th className="py-2 font-medium">Last IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lockedAccounts.map((u, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-4">{u.email}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{u.tenant ?? '—'}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="secondary">{u.failedAttempts}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">{fmtTime(u.lockedUntil)}</td>
                      <td className="py-2 font-mono text-xs text-muted-foreground">{u.lastIp ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security event feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4" /> Recent security events
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data?.recentEvents.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No recent authentication events recorded.
            </p>
          ) : (
            <div className="space-y-1">
              {data.recentEvents.map((e, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted/50"
                >
                  <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${actionTone(e.action)}`}>
                    {e.action}
                  </span>
                  <span className="flex-1 truncate text-muted-foreground">
                    {e.who ?? '—'}
                    {e.tenant ? ` · ${e.tenant}` : ''}
                    {e.ip ? ` · ${e.ip}` : ''}
                  </span>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">{fmtTime(e.at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecurityPage;
